import React, { forwardRef, createContext, HTMLAttributes, useState, useContext, useEffect } from 'react';
import classNames from 'classnames';
import { menu_base_css } from './styles';

export interface MenuContextProps {
  activeMenu?: string;
  activeMenuGroup?: string;
  setActiveMenu: (id?: string) => void;
  setActiveMenuGroup: (id?: string) => void;
  openMenuGroup?: string;
  setOpenMenuGroup: (groupId?: string) => void;
  onClickMenu: ({ id, groupId }: { id?: string; groupId?: string }) => void;
  onMenuGroupOpenChange?: (groupId: string) => void;
  toggleMode: boolean;
  dense: boolean;
}

export const MenuContext = createContext<MenuContextProps | null>(null);

export const useMenuContext = () => {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('MenuContext is not defined');
  }
  return context;
};

export interface MenuProps extends HTMLAttributes<HTMLUListElement> {
  selectedMenu?: MenuContextProps['activeMenu'];
  onClickMenu?: (id: MenuContextProps['activeMenu']) => void;
  /**
   * MenuGroup 서브 메뉴 open/close시 이벤트를 전달합니다.
   */
  onMenuGroupOpenChange?: (id: MenuContextProps['activeMenuGroup']) => void;
  /**
   * Toggle 기능 사용 여부
   * @default false
   */
  toggleMode?: MenuContextProps['toggleMode'];
  /** @default true */
  dense?: boolean;
}

export const Menu = forwardRef<HTMLUListElement, MenuProps>(
  (
    {
      className,
      children,
      selectedMenu,
      onClickMenu,
      onMenuGroupOpenChange,
      toggleMode = false,
      dense = true,
      ...props
    },
    ref,
  ) => {
    const [activeMenu, setActiveMenu] = useState<string | undefined>(selectedMenu);
    const [activeMenuGroup, setActiveMenuGroup] = useState<string | undefined>();
    const [openMenuGroup, setOpenMenuGroup] = useState<string | undefined>();

    const handleClickMenu: MenuContextProps['onClickMenu'] = ({ id, groupId }) => {
      setActiveMenu(id);
      setActiveMenuGroup(groupId ?? undefined);
      setOpenMenuGroup(groupId ?? undefined);
      onClickMenu?.(id);
    };

    useEffect(() => {
      if (selectedMenu && selectedMenu !== activeMenu) {
        setActiveMenu(selectedMenu);
        setActiveMenuGroup(undefined);
        setOpenMenuGroup(undefined);
      }
    }, [selectedMenu]);

    return (
      <MenuContext.Provider
        value={{
          activeMenu,
          setActiveMenu,
          activeMenuGroup,
          setActiveMenuGroup,
          openMenuGroup,
          setOpenMenuGroup,
          onClickMenu: handleClickMenu,
          onMenuGroupOpenChange,
          toggleMode,
          dense,
        }}
      >
        <ul role='menu' className={classNames('pds-menu', className)} css={menu_base_css} {...props} ref={ref}>
          {children}
        </ul>
      </MenuContext.Provider>
    );
  },
);
